import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Zap, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function AuthPage() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register, user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  useEffect(() => {
    const m = params.get("mode");
    if (m === "register" || m === "login") setMode(m);
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = mode === "login" ? await login(email, password) : await register(name, email, password);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 grid-texture opacity-30" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-[#EC1E9C]/15 blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-[#00FFC2]/12 blur-3xl" />
      <div className="absolute top-4 right-4 z-10"><LanguageSwitcher /></div>
      <div className="relative w-full max-w-md rounded-2xl border border-[#243047] bg-[#121824] p-8 fade-up">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#EC1E9C] to-[#00FFC2] flex items-center justify-center">
            <Zap className="w-5 h-5 text-[#03110B]" strokeWidth={2.5} />
          </div>
          <span className="font-heading font-extrabold text-xl text-white tracking-tight">{t("brand")}</span>
        </div>

        <div className="flex gap-1 p-1 rounded-lg bg-[#0A0D14] border border-[#243047] mb-6">
          {["login", "register"].map((m) => (
            <button key={m} onClick={() => { setMode(m); setError(""); }} data-testid={`auth-tab-${m}`}
              className={`flex-1 py-2 rounded-md text-sm font-semibold transition ${
                mode === m ? "bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B]" : "text-[#94A3B8] hover:text-white"
              }`}>
              {m === "login" ? t("auth.login") : t("auth.register")}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <Field label={t("auth.name")}>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="auth-name-input"
                className="input" placeholder={t("auth.namePh")} />
            </Field>
          )}
          <Field label={t("auth.email")}>
            <input type={mode === "login" ? "text" : "email"} required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-email-input"
              className="input" placeholder={mode === "login" ? "you@email.com" : "you@email.com"} autoComplete="username" />
          </Field>
          <Field label={t("auth.password")}>
            <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="auth-password-input"
              className="input" placeholder={t("auth.passPh")} />
          </Field>

          {error && <div data-testid="auth-error" className="text-sm text-[#EC1E9C] bg-[#EC1E9C]/10 border border-[#EC1E9C]/30 rounded-lg px-3 py-2">{error}</div>}

          <button type="submit" disabled={loading} data-testid="auth-submit-btn"
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] font-bold py-3 hover:brightness-110 transition disabled:opacity-60">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === "login" ? t("auth.submitLogin") : t("auth.submitRegister")}
          </button>
        </form>
        {mode === "register" && (
          <p className="text-xs text-[#64748B] mt-4 text-center">{t("auth.freeNote")}</p>
        )}
      </div>
      <style>{`.input{width:100%;background:#0A0D14;border:1px solid #243047;border-radius:.6rem;padding:.65rem .8rem;color:#F8FAFC;font-size:.9rem;outline:none;transition:border-color .2s}.input:focus{border-color:#00FFC2}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
